// `extension-alert-client-contact-snippet` — a contact's profile.
//
// ⚠️ A SEPARATE COMPONENT ON PURPOSE, AND IT RENDERS THE CANDIDATE SNIPPET'S CLASSES. The root
// div is `.extension-alert-candidate-snippet` and every class inside it is the candidate
// sheet's, so nothing new is styled; the host ELEMENT is also already in that sheet's type
// selector (`extension-alert-candidate-snippet, extension-alert-external-candidate-snippet,
// extension-alert-client-contact-snippet`). What is NOT shared is the body: upstream keeps two
// templates because this one has a Create-in-ATS button, a get-contact-details block, one
// LinkedIn link where the candidate has three social circles, and a highlights column with the
// ATS card and nothing else — no matching summary, no master profile, and no "No highlights"
// fallback. Branching the candidate component through all of that would be a worse file than
// two, which is the judgement upstream already made.
import { createElement, useRef, useState, type CSSProperties, type ReactElement, type ReactNode } from 'react'
import { UibTooltipPopup, useHoverTooltip } from '../common/UibTooltip.tsx'
import { ProfileActionsStrip } from './ProfileActionsStrip.tsx'
import { ClientContactAtsInfoCard, type ClientContactAtsInfo } from './ClientContactAtsInfoCard.tsx'
import { clientContactName, type NamedEntity } from './candidateName.ts'
import { lastPositionInfo, nameInitials } from './CandidateSnippet.tsx'
import { contactDetailsDisplay } from './contactDetailsDisplay.ts'
import type { ProfileActionDefinition } from './profileActions.ts'

export interface SnippetClientContact extends NamedEntity {
  id: number
  lastPosition?: string | null
  lastCompany?: string | null
  location?: string | null
  email?: string | null
  phoneNumber?: string | null
  /** What the snippet shows, joined; the slim list row reads `phoneNumber` instead. */
  phoneNumbers?: readonly string[] | null
  /** `ClientContactDetailsSource`; 0 is the ATS. */
  emailSource?: number | null
  phoneNumberSource?: number | null
  lastTimeUserRequestedContactDetails?: number | null
  linkedinUrl?: string | null
  favorite?: boolean | null
  hidden?: boolean | null
  atsInfo?: ClientContactAtsInfo | null
}

/**
 * `hasExistingContactDetails` — whether the contact already has a way to reach them.
 *
 * ⚠️ UPSTREAM READS `phoneNumbers` (PLURAL, an array) AND THIS SHAPE HAS `phoneNumber`. The DTO
 * carries both — the list's slim row uses the singular and the snippet's own check uses the
 * array — so a port that tests only one of them mislabels a contact whose details came from the
 * other field. Both are read here, and the label is the only thing that depends on it.
 */
export function hasContactDetails(contact: SnippetClientContact | undefined): boolean {
  if (contact === undefined) return false
  const email = contact.email ?? ''
  const phone = contact.phoneNumber ?? ''
  return email.length > 0 || phone.length > 0 || (contact.phoneNumbers?.length ?? 0) > 0
}

export interface ClientContactSnippetProps {
  contact?: SnippetClientContact
  actions?: readonly ProfileActionDefinition[]
  atsName?: string
  /** `ctrl.atsLabel` — every place the snippet NAMES the ATS (5cfd3ef899). Defaults to `atsName`. */
  atsLabel?: string
  /** See `AtsInfoCard`: a session with no extension shell reports IN the ATS. */
  inAts?: boolean
  /**
   * `featureFlags.CREATE_CLIENT_CONTACT_IN_ATS && careero.createClientContactInAtsEnabled` —
   * swaps the "View in ATS" button for "Create in ATS", and independently shows the ATS card's
   * own header link. No candidate equivalent.
   */
  createInAtsEnabled?: boolean
  /**
   * The Create-in-ATS container's `ng-class="{ 'client-contact-snippet-button-disabled': … }"` —
   * and, being the same expression, its `tooltip-enable` (`isCreateInAtsButtonDisabled`).
   */
  createInAtsDisabled?: boolean
  /** `getDisabledCreateClientContactModalTooltip()` — `tooltip-placement="top-left"`, on the body. */
  createInAtsTooltip?: string | undefined
  /** `click-and-disable="ctrl.onCreateInATS()"` — see `useCreateClientContactInAts`. */
  onCreateInAts?: () => void
  /** False once the ATS info call settles; the highlights column is absent until then. */
  loadingSummaryAndPreview?: boolean
  viewMore?: boolean
  hideShowMoreLessButton?: boolean
  /** `careero.featureFlags.UPDATE_CONTACT_DETAILS_ENABLED` — the CTA exists only with it. */
  updateContactDetailsEnabled?: boolean
  /**
   * `requestContactDetailsEnabled` — false greys the button out, with a tooltip saying why
   * ("No updated details available", "Monthly contact lookup limit reached."). Both answers
   * come from the enrichment flow, so it is true until that flow lands.
   */
  requestContactDetailsEnabled?: boolean
  /** `featureFlags.HIDE_CLIENT_CONTACT_CONTACT_DETAILS` — masks details by source (`contactDetailsDisplay`). */
  hideContactDetailsEnabled?: boolean
  onGetContactDetails?: () => void
  maxSnippetWidth?: number
  maxWidth?: number
  sideBySide?: boolean
  onToggleViewMore?: () => void
  onViewInAts?: () => void
  onAction?: (action: ProfileActionDefinition) => void
  /** `onSocialUrlClick('linkedin')` on the LinkedIn circle — the template's only social link. */
  onSocialUrlClick?: () => void
  /** `on-scroll="ctrl.onScrollOnce($event)"` on the scrolling `.snippet-content`. */
  onContentScroll?: (element: HTMLElement) => void
  children?: ReactNode
}

export function ClientContactSnippet({
  contact,
  actions = [],
  atsName,
  atsLabel = atsName,
  inAts = true,
  createInAtsEnabled = false,
  createInAtsDisabled = false,
  createInAtsTooltip,
  onCreateInAts,
  loadingSummaryAndPreview = false,
  viewMore = false,
  hideShowMoreLessButton = false,
  updateContactDetailsEnabled = false,
  requestContactDetailsEnabled = true,
  hideContactDetailsEnabled = false,
  onGetContactDetails,
  maxSnippetWidth,
  maxWidth,
  sideBySide = false,
  onToggleViewMore,
  onViewInAts,
  onAction,
  onSocialUrlClick,
  onContentScroll,
  children,
}: ClientContactSnippetProps): ReactElement {
  // Hooks before the loading branch's early return.
  const createInAtsHost = useRef<HTMLDivElement>(null)
  const createInAtsHover = useHoverTooltip(createInAtsTooltip, createInAtsDisabled)

  const style: CSSProperties = {}
  if (maxWidth !== undefined) {
    style.maxWidth = maxWidth
    style.flexBasis = sideBySide ? maxWidth : 'auto'
  }

  const host = (content: ReactNode): ReactElement =>
    createElement('extension-alert-client-contact-snippet', { style }, content)

  if (contact === undefined) {
    return host(
      <div className="extension-alert-candidate-snippet">
        <div className="snippet-loading-container-outer">
          <div className="snippet-loading-container">
            <i className="fa fa-spinner fa-spin" />
          </div>
        </div>
      </div>,
    )
  }

  const positionInfo = lastPositionInfo(contact)
  const hasLinkedin = contact.linkedinUrl !== undefined && contact.linkedinUrl !== null && contact.linkedinUrl !== ''

  // ⚠️ THE CONTACT LINES ARE NOT THE CANDIDATE'S. There is no phone-or-socials row here: the
  // socials moved into the header as a single LinkedIn circle, and email and phone each render
  // on their own with an optional "Added by Woo" tag when the value did not come from the ATS.
  // `calculateShowContactDetails(true)`: the on-load state. The request flow behind "Get phone and
  // email" is not ported yet, so `revealed` is never passed.
  const details = contactDetailsDisplay(contact, hideContactDetailsEnabled, Date.now())
  const contactInfo = (
    <div>
      {contact.location === undefined || contact.location === null || contact.location === '' ? null : (
        <div className="candidate-contact-info-item">
          <i className="fa fa-map-marker fa-sm fa-fw" />
          &nbsp;{contact.location}
        </div>
      )}
      {details.email === null ? null : (
        <div className="candidate-contact-info-item">
          <i className="fas fa-envelope fa-sm fa-fw" />
          &nbsp;{details.email}
          {details.emailAddedByWoo ? <span className="contact-data-added-by-woo">Added by Woo</span> : null}
        </div>
      )}
      {details.phone === null ? null : (
        <div className="candidate-contact-info-item candidate-contact-info-phone-social">
          <div className="candidate-contact-info-phone">
            <i className="fas fa-mobile-alt fa-sm fa-fw" />
            &nbsp;{details.phone}
            {details.phoneAddedByWoo ? <span className="contact-data-added-by-woo">Added by Woo</span> : null}
          </div>
        </div>
      )}
    </div>
  )

  return host(
    <div className="extension-alert-candidate-snippet">
      <div className="candidate-alert-info">
        <div className={`candidate-snippet-header${viewMore ? ' row-view' : ''}`}>
          <div
            className="candidate-basic-info-wrap"
            style={{ flexBasis: viewMore && maxSnippetWidth !== undefined ? maxSnippetWidth : 'auto' }}
          >
            <div className="candidate-image-wrap">
              <div className="candidate-image">{nameInitials(contact.name)}</div>
            </div>
            <div className="candidate-basic-info-and-actions">
              <div className="candidate-basic-info">
                <div className="info-wrap">
                  <div className="candidate-name" title={clientContactName(contact)}>
                    {clientContactName(contact)}
                  </div>
                  {positionInfo === '' ? null : (
                    <div className="candidate-last-job-title" title={positionInfo}>
                      {positionInfo}
                    </div>
                  )}
                </div>
                {hideShowMoreLessButton ? null : (
                  <div className="op-info-wrap">
                    <a
                      className={`snippet-action op-info snippet-action-btn ${viewMore ? 'op-less-info' : 'op-more-info'}`}
                      role="button"
                      tabIndex={0}
                      onClick={onToggleViewMore}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter' || event.key === ' ') onToggleViewMore?.()
                      }}
                    >
                      <span className="snippet-action-btn-text">{viewMore ? 'Less' : 'More'}</span>
                      &nbsp;
                      <i
                        className={`snippet-action-btn-icon fal rtl-hidden ${viewMore ? 'fa-chevron-left' : 'fa-chevron-right'}`}
                      />
                    </a>
                  </div>
                )}
              </div>
              <div className="candidate-links-and-actions">
                {/* ⚠️ TWO BUTTONS, ONE SLOT, AND ONLY THE FIRST TAKES THE ORANGE MODIFIER.
                    "Create in ATS" has no `view-in-ats-orange` branch at all — a tenant with
                    contact creation on gets a plain button whether or not it is in the ATS.
                    Its disabled state and tooltip hang off the CONTAINER, and the class is what
                    stops the click: `.client-contact-snippet-button-disabled button` is
                    `pointer-events: none`, which is also why the tooltip is on the div. */}
                {createInAtsEnabled ? (
                  <div
                    ref={createInAtsHost}
                    className={`op-view-in-ats-container${createInAtsDisabled ? ' client-contact-snippet-button-disabled' : ''}`}
                    onMouseEnter={createInAtsHover.onMouseEnter}
                    onMouseLeave={createInAtsHover.onMouseLeave}
                  >
                    <button type="button" className="op-view-in-ats cursor-pointer" onClick={onCreateInAts}>
                      Create in ATS
                    </button>
                  </div>
                ) : (
                  <div className="op-view-in-ats-container">
                    <button
                      type="button"
                      className={`op-view-in-ats cursor-pointer${inAts ? '' : ' view-in-ats-orange'}`}
                      onClick={onViewInAts}
                    >
                      View in ATS
                    </button>
                  </div>
                )}
                {createInAtsEnabled && createInAtsHover.open ? (
                  <UibTooltipPopup host={createInAtsHost} placement="top-left">
                    {createInAtsTooltip}
                  </UibTooltipPopup>
                ) : null}
                {!hasLinkedin ? null : (
                  <div className="candidate-contact-info-social-url-container">
                    <a
                      className="candidate-contact-info-social-url fa-stack fa-1x"
                      href={contact.linkedinUrl ?? ''}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => {
                        onSocialUrlClick?.()
                      }}
                    >
                      <i className="fas fa-circle fa-stack-2x" />
                      <i className="fab fa-linkedin-in fa-stack-1x url-inner-icon" />
                    </a>
                  </div>
                )}
                <div className="candidate-actions">
                  <ProfileActionsStrip entity={contact} actions={actions} maxActionAmount={4} isDropdownEnabled onAction={onAction} />
                </div>
              </div>
            </div>
          </div>
          {!viewMore ? null : <hr className="divider" />}
          {!viewMore ? null : <div className="candidate-contact-info-wrap">{contactInfo}</div>}
          {/* ⚠️ THE CTA IS PART OF THE **More** HEADER AND OF NOTHING ELSE. Its `ng-if` is
              `isInViewMoreState() && updateContactDetailsEnabled`, so it exists on one state of
              one screen, behind a tenant flag — which is why it stayed invisible until the More
              references were captured open, and then it was the last 759 pixels on the screen.

              ⚠️ AND IT IS NOT WIRED, deliberately. `onGetContactDetailsClicked` spends the
              tenant's contact-lookup credits against a third-party enrichment provider and
              writes the answer onto the contact — the same class of outward-facing write as the
              composer's Send. It renders, it carries its label, and `onGetContactDetails` is
              where the flow lands. The progress and result states come with it: without a
              request there is nothing to report. */}
          {!viewMore || !updateContactDetailsEnabled ? null : (
            <div className="client-contact-snippet-profile-actions">
              <div>
                <div
                  className={`client-contact-get-latest-details-container${
                    requestContactDetailsEnabled ? '' : ' client-contact-snippet-button-disabled'
                  }`}
                >
                  <button
                    type="button"
                    onClick={() => {
                      onGetContactDetails?.()
                    }}
                  >
                    {/* `getContactDetailsCtaLabel` — the contact ALREADY having an email or a
                        phone number changes the verb: there is nothing to "get". */}
                    {hasContactDetails(contact) ? 'Find newer details' : 'Get phone and email'}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
        <div className="candidate-snippet-content-wrap">
          <div
            className="snippet-content nicer-scroller mac-style"
            style={{ flexBasis: maxSnippetWidth }}
            onScroll={(event) => {
              onContentScroll?.(event.currentTarget)
            }}
          >
            {viewMore ? null : <div className="candidate-contact-info-wrap">{contactInfo}</div>}
            {/* ⚠️ NO "No highlights" FALLBACK AND NO SPINNER HERE. The candidate template has
                three branches over this column; the contact template has ONE `ng-if`, so a
                contact with no ATS info gets an empty column rather than a sentence. */}
            {loadingSummaryAndPreview ? null : (
              <div className="highlights-wrap">
                <div className="highlights-scroll-wrap">
                  <div className="highlights-matching-summary-container">
                    {/* ⚠️ GATED ON THE DATA ALONE — there is no `atsInfoEnabled` feature check
                        on this side, where the candidate card needs one. */}
                    {contact.atsInfo === undefined || contact.atsInfo === null ? null : (
                      <div className="highlight-section ats-info-container">
                        <ClientContactAtsInfoCard
                          atsInfo={contact.atsInfo}
                          atsName={atsName}
                          {...(atsLabel === undefined ? {} : { atsLabel })}
                          showViewInAts={!inAts || createInAtsEnabled}
                          {...(onViewInAts === undefined ? {} : { onViewInAts })}
                        />
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
          {!viewMore ? null : <div className="snippet-view-wrap">{children}</div>}
        </div>
      </div>
    </div>,
  )
}
